import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { Card } from '../types/card';
import type { CardLinkDoc, CardLinkEntry, CardLinkEntryType } from '../types/cardsLink';
import { CardService } from '../services/cardService';
import { MilestoneService } from '../services/milestoneService';
import { CardsLinkService } from '../services/cardsLinkService';
import type { Milestone } from '../types/milestone';
import CreateRewardForm from './CreateRewardForm';
import CreateMilestoneForm from './CreateMilestoneForm';
import { useAuth } from '../context/AuthContext';

const CardDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [card, setCard] = useState<Card | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [formData, setFormData] = useState<Partial<Card>>({});

  // Rewards state
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [rewardsError, setRewardsError] = useState<string | null>(null);
  const [rewards, setRewards] = useState<any[]>([]);
  const [rewardsPage, setRewardsPage] = useState(1);
  const [rewardsTotal, setRewardsTotal] = useState(0);
  const [rewardsPages, setRewardsPages] = useState(1);
  const [selectedReward, setSelectedReward] = useState<any | null>(null);
  const [rewardDetailLoading, setRewardDetailLoading] = useState(false);

  // Create reward state
  const [showCreateReward, setShowCreateReward] = useState(false);
  const [createRewardLoading, setCreateRewardLoading] = useState(false);
  const [createRewardError, setCreateRewardError] = useState<string | null>(null);
  const [createRewardSuccess, setCreateRewardSuccess] = useState(false);

  // Milestones state
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [milestonesError, setMilestonesError] = useState<string | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [milestonesPage, setMilestonesPage] = useState(1);
  const [milestonesTotal, setMilestonesTotal] = useState(0);
  const [milestonesPages, setMilestonesPages] = useState(1);

  // Create milestone state
  const [showCreateMilestone, setShowCreateMilestone] = useState(false);
  const [createMilestoneLoading, setCreateMilestoneLoading] = useState(false);
  const [createMilestoneError, setCreateMilestoneError] = useState<string | null>(null);
  const [createMilestoneSuccess, setCreateMilestoneSuccess] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'rewards' | 'milestones' | 'links'>('overview');

  const isAdmin = user?.role === 'admin';

  // Links state (CardsLink)
  const [cardLinkLoading, setCardLinkLoading] = useState<boolean>(false);
  const [cardLinkError, setCardLinkError] = useState<string | null>(null);
  const [cardLink, setCardLink] = useState<CardLinkDoc | null>(null);
  const [cardLinkEditing, setCardLinkEditing] = useState<boolean>(false);
  const [cardLinkSaving, setCardLinkSaving] = useState<boolean>(false);
  const [cardLinkBankName, setCardLinkBankName] = useState<string>('');
  const [cardLinkCreditName, setCardLinkCreditName] = useState<string>('');

  const [newEntryType, setNewEntryType] = useState<CardLinkEntryType>('url');
  const [newEntryValue, setNewEntryValue] = useState<string>('');
  const [entrySaving, setEntrySaving] = useState<boolean>(false);

  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editingEntryType, setEditingEntryType] = useState<CardLinkEntryType>('url');
  const [editingEntryValue, setEditingEntryValue] = useState<string>('');

  useEffect(() => {
    if (id) {
      fetchCardDetails(id);
    }
  }, [id]);

  useEffect(() => {
    if (card?._id) {
      fetchRewards(card._id, rewardsPage, 100);
      fetchMilestones(card._id, milestonesPage, 100);
    }
  }, [card?._id, rewardsPage, milestonesPage]);

  useEffect(() => {
    if (!card?._id) return;

    let cancelled = false;
    const run = async () => {
      try {
        setCardLinkLoading(true);
        setCardLinkError(null);
        const res = await CardsLinkService.getByCardId(card._id);
        if (cancelled) return;
        setCardLink(res.data || null);
        setCardLinkBankName(res.data?.bank_name || card.bankName || '');
        setCardLinkCreditName(res.data?.credit_name || card.name || '');
        setCardLinkEditing(false);
      } catch (e: any) {
        if (cancelled) return;
        // Treat not-found as "no links yet"
        if (e && typeof e === 'object' && (e as any).status === 404) {
          setCardLink(null);
          setCardLinkBankName(card.bankName || '');
          setCardLinkCreditName(card.name || '');
          setCardLinkEditing(false);
          setCardLinkError(null);
          return;
        }
        setCardLink(null);
        setCardLinkError(e instanceof Error ? e.message : 'Failed to load links');
      } finally {
        if (!cancelled) setCardLinkLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [card?._id, card?.bankName, card?.name]);

  const fetchRewards = async (cardObjectId: string, page: number, limit: number) => {
    try {
      setRewardsLoading(true);
      setRewardsError(null);
      const response = await CardService.getRewards({ cardId: cardObjectId, page, limit });
      setRewards(response.data || []);
      setRewardsTotal(response.total || 0);
      setRewardsPages(response.pages || 1);
    } catch (err) {
      console.error('Error fetching rewards:', err);
      setRewardsError(err instanceof Error ? err.message : 'Failed to load rewards');
    } finally {
      setRewardsLoading(false);
    }
  };

  const openRewardDetail = async (rewardId: string) => {
    try {
      setRewardDetailLoading(true);
      setSelectedReward(null);
      const response = await CardService.getRewardById(rewardId);
      setSelectedReward(response.data);
    } catch (err) {
      console.error('Error fetching reward detail:', err);
    } finally {
      setRewardDetailLoading(false);
    }
  };

  const closeRewardDetail = () => setSelectedReward(null);

  const handleCreateRewardSuccess = () => {
    setCreateRewardSuccess(true);
    // Refresh the rewards list
    if (card?._id) {
      fetchRewards(card._id, rewardsPage, 100);
    }
    // Hide success message after 3 seconds
    setTimeout(() => setCreateRewardSuccess(false), 3000);
  };

  const fetchMilestones = async (cardObjectId: string, page: number, limit: number) => {
    try {
      setMilestonesLoading(true);
      setMilestonesError(null);
      const response = await MilestoneService.getMilestonesByCard(cardObjectId, { page, limit });
      
      if (response.success && response.data) {
        setMilestones(response.data);
        setMilestonesTotal(response.total || response.data.length);
        setMilestonesPages(response.pages || 1);
      } else {
        setMilestonesError('Failed to fetch milestones');
      }
    } catch (err) {
      console.error('Error fetching milestones:', err);
      setMilestonesError(err instanceof Error ? err.message : 'Failed to fetch milestones');
    } finally {
      setMilestonesLoading(false);
    }
  };

  const handleCreateMilestoneSuccess = () => {
    setCreateMilestoneSuccess(true);
    // Refresh the milestones list
    if (card?._id) {
      fetchMilestones(card._id, milestonesPage, 100);
    }
    // Hide success message after 3 seconds
    setTimeout(() => setCreateMilestoneSuccess(false), 3000);
  };

  const fetchCardDetails = async (cardId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await CardService.getCardById(cardId);
      setCard(response.data);
      
      // Initialize form data with current card data
      setFormData({
        active: response.data.active,
        earnRates: response.data.earnRates,
        annualFees: response.data.annualFees,
      });
      
      // Update page title
      document.title = `${response.data.name} - ${response.data.bankName} | Rewards Admin`;
    } catch (err) {
      console.error('Error fetching card details:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setUpdateSuccess(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setUpdateSuccess(false);
    // Reset form data to original card data
    if (card) {
      setFormData({
        active: card.active,
        earnRates: card.earnRates,
        annualFees: card.annualFees,
      });
    }
  };

  const handleSave = async () => {
    if (!card) return;
    
    try {
      setIsUpdating(true);
      setError(null);
      
      const response = await CardService.updateCard(card._id, formData);
      setCard(response.data);
      setIsEditing(false);
      setUpdateSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating card:', err);
      setError(err instanceof Error ? err.message : 'Failed to update card');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = field.split('.');
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // Inline toggle handlers for Active and Verified
  const handleToggleActive = async () => {
    if (!card) return;
    try {
      setIsUpdating(true);
      const nextActive = !(formData.active !== undefined ? formData.active : card.active);
      const response = await CardService.updateCard(card._id, { active: nextActive });
      setCard(response.data);
      setFormData(prev => ({ ...prev, active: response.data.active }));
    } catch (err) {
      console.error('Error toggling active:', err);
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleVerify = async () => {
    if (!card) return;
    try {
      setIsUpdating(true);
      const current = card.isVerify === 'true';
      const next = (!current).toString();
      const response = await CardService.updateCard(card._id, { isVerify: next as any });
      setCard(response.data);
      setFormData(prev => ({ ...prev, active: response.data.active }));
    } catch (err) {
      console.error('Error toggling verify:', err);
      setError(err instanceof Error ? err.message : 'Failed to update verification');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/cards"
            className="flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Cards
          </Link>
        </div>
        
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-500">Loading card details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/cards"
            className="flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Cards
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-red-800">Unable to load card details</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button 
                onClick={() => id && fetchCardDetails(id)}
                className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/cards"
            className="flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Cards
          </Link>
        </div>
        
        <div className="text-center py-16">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Card not found</h3>
          <p className="mt-2 text-gray-500">The card you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success notifications */}
      {updateSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                Card updated successfully!
              </p>
            </div>
          </div>
        </div>
      )}

      {createRewardSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                Reward created successfully!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header with breadcrumb */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/cards"
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Cards
          </Link>
          <div className="text-gray-300">/</div>
          <h1 className="text-2xl font-bold text-gray-900">{card.name}</h1>
          {isEditing && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
              Editing
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {!isEditing ? (
            <>
              <button
                onClick={handleToggleActive}
                disabled={isUpdating}
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                  (formData.active !== undefined ? formData.active : card.active)
                    ? 'bg-green-100 text-green-800 border-green-200' 
                    : 'bg-red-100 text-red-800 border-red-200'
                } ${isUpdating ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90'}`}
                title="Toggle Active"
              >
                {(formData.active !== undefined ? formData.active : card.active) ? 'Active' : 'Inactive'}
              </button>
              <button
                onClick={handleToggleVerify}
                disabled={isUpdating}
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                  card.isVerify === 'true'
                    ? 'bg-blue-100 text-blue-800 border-blue-200' 
                    : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                } ${isUpdating ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90'}`}
                title="Toggle Verified"
              >
                {card.isVerify === 'true' ? 'Verified' : 'Unverified'}
              </button>
              <button
                onClick={handleEdit}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Card
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                disabled={isUpdating}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isUpdating}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('overview')}
            className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
              activeTab === 'overview'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Basic Information
          </button>
          <button
            onClick={() => setActiveTab('rewards')}
            className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium flex items-center gap-2 ${
              activeTab === 'rewards'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span>Rewards</span>
            {rewardsTotal > 0 && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                activeTab === 'rewards'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {rewardsTotal}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('milestones')}
            className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium flex items-center gap-2 ${
              activeTab === 'milestones'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span>Milestones</span>
            {milestonesTotal > 0 && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                activeTab === 'milestones'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {milestonesTotal}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('links')}
            className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium flex items-center gap-2 ${
              activeTab === 'links'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span>Links</span>
            {Array.isArray(cardLink?.entries) && cardLink!.entries!.length > 0 && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === 'links' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {cardLink!.entries!.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Main details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Card Name</label>
                  <input
                    type="text"
                    value={card.name}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={card.bankName}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Card ID</label>
                  <input
                    type="text"
                    value={card.id}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Slug</label>
                  <input
                    type="text"
                    value={card.bankSlug}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                    readOnly
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
                <div className="flex flex-wrap gap-2">
                  {card.categories.map((category, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Annual Fees */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Annual Fees</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary Fee</label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={formData.annualFees?.primary || 0}
                      onChange={(e) => handleInputChange('annualFees.primary', parseInt(e.target.value) || 0)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter primary fee amount"
                    />
                  ) : (
                    <input
                      type="text"
                      value={card.annualFees?.primary !== null ? `₹${card.annualFees.primary?.toLocaleString()}` : 'Free'}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                      readOnly
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Add-on Fee</label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={formData.annualFees?.addOn || 0}
                      onChange={(e) => handleInputChange('annualFees.addOn', parseInt(e.target.value) || 0)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter add-on fee amount"
                    />
                  ) : (
                    <input
                      type="text"
                      value={card.annualFees?.addOn !== null ? `₹${card.annualFees.addOn?.toLocaleString()}` : 'Free'}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                      readOnly
                    />
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Waiver Condition</label>
                {isEditing ? (
                  <textarea
                    value={formData.annualFees?.waiver?.condition || ''}
                    onChange={(e) => handleInputChange('annualFees.waiver.condition', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={2}
                    placeholder="Enter waiver condition"
                  />
                ) : (
                  <textarea
                    value={card.annualFees?.waiver?.condition || 'No waiver condition specified'}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                    rows={2}
                    readOnly
                  />
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Spend Threshold for Waiver</label>
                {isEditing ? (
                  <input
                    type="number"
                    value={formData.annualFees?.waiver?.spendThreshold || 0}
                    onChange={(e) => handleInputChange('annualFees.waiver.spendThreshold', parseInt(e.target.value) || 0)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter spend threshold amount"
                  />
                ) : (
                  <input
                    type="text"
                    value={card.annualFees?.waiver?.spendThreshold ? `₹${card.annualFees.waiver.spendThreshold.toLocaleString()}` : 'Not specified'}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                    readOnly
                  />
                )}
              </div>
            </div>
          </div>

          {/* Earn Rates */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Earn Rates</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-3">Default Rate</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Rate (Per)</label>
                    {isEditing ? (
                      <input
                        type="number"
                        value={formData.earnRates?.default?.per || 1}
                        onChange={(e) => handleInputChange('earnRates.default.per', parseInt(e.target.value) || 1)}
                        className="block w-full px-3 py-2 border border-blue-200 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Rate per amount"
                      />
                    ) : (
                      <input
                        type="text"
                        value={`${card.earnRates?.default?.per || 1} ${card.earnRates?.default?.unit || 'rp'}`}
                        className="block w-full px-3 py-2 border border-blue-200 rounded-md shadow-sm bg-white"
                        readOnly
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Per Amount</label>
                    {isEditing ? (
                      <input
                        type="number"
                        value={formData.earnRates?.default?.perAmount || 100}
                        onChange={(e) => handleInputChange('earnRates.default.perAmount', parseInt(e.target.value) || 100)}
                        className="block w-full px-3 py-2 border border-blue-200 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Amount threshold"
                      />
                    ) : (
                      <input
                        type="text"
                        value={`₹${card.earnRates?.default?.perAmount || 100}`}
                        className="block w-full px-3 py-2 border border-blue-200 rounded-md shadow-sm bg-white"
                        readOnly
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Unit</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.earnRates?.default?.unit || 'rp'}
                        onChange={(e) => handleInputChange('earnRates.default.unit', e.target.value)}
                        className="block w-full px-3 py-2 border border-blue-200 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Reward unit"
                      />
                    ) : (
                      <input
                        type="text"
                        value={card.earnRates?.default?.unit || 'rp'}
                        className="block w-full px-3 py-2 border border-blue-200 rounded-md shadow-sm bg-white"
                        readOnly
                      />
                    )}
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-blue-700 mb-1">Note</label>
                  {isEditing ? (
                    <textarea
                      value={formData.earnRates?.default?.note || ''}
                      onChange={(e) => handleInputChange('earnRates.default.note', e.target.value)}
                      className="block w-full px-3 py-2 border border-blue-200 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={2}
                      placeholder="Add note about this earn rate"
                    />
                  ) : (
                    <textarea
                      value={card.earnRates?.default?.note || 'No note provided'}
                      className="block w-full px-3 py-2 border border-blue-200 rounded-md shadow-sm bg-white"
                      rows={2}
                      readOnly
                    />
                  )}
                </div>
              </div>
              
              {/* {card.earnRates?.other && card.earnRates.other.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Other Rates</h4>
                  {card.earnRates.other.map((rate, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Rate</label>
                          <input
                            type="text"
                            value={`${rate.per} ${rate.unit}`}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white"
                            readOnly
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Per Amount</label>
                          <input
                            type="text"
                            value={`₹${rate.perAmount}`}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white"
                            readOnly
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                          <input
                            type="text"
                            value={rate.unit}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white"
                            readOnly
                          />
                        </div>
                      </div>
                      {rate.note && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                          <textarea
                            value={rate.note}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white"
                            rows={2}
                            readOnly
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )} */}
            </div>
          </div>
            </>
          )}

          {/* Rewards Tab */}
          {activeTab === 'rewards' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-medium text-gray-900">Rewards</h3>
                {rewardsTotal > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                    {rewardsTotal} total
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowCreateReward(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Reward
              </button>
            </div>
            <div className="p-6">
              {rewardsLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-500">Loading rewards...</p>
                </div>
              ) : rewardsError ? (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">
                  {rewardsError}
                </div>
              ) : rewards.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h4 className="mt-4 text-sm font-medium text-gray-900">No rewards found</h4>
                  <p className="mt-1 text-sm text-gray-500">No rewards are currently linked to this card.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rewards.map((reward) => (
                    <div key={reward._id} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                            {reward.logo?.url ? (
                              <img src={reward.logo.url} alt={reward.logo.alt || reward.name} className="h-10 w-10 object-cover" />
                            ) : (
                              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v16H4z" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{reward.name}</h4>
                          <p className="text-xs text-gray-500">{reward.categoryPrimary} {reward.subcategory ? `• ${reward.subcategory}` : ''}</p>
                          <div className="mt-2 text-sm text-gray-700">
                            {reward.rewards?.description || 'Reward details not available'}
                          </div>
                          {reward.tags && reward.tags.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {reward.tags.slice(0, 6).map((t: string, i: number) => (
                                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                  {t}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          reward.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {reward.status || 'unknown'}
                        </span>
                        <button
                          className="ml-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                          onClick={() => navigate(`/rewards/json-edit/${encodeURIComponent(String(reward._id))}`)}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Rewards pagination (if needed later) */}
              {rewardsPages > 1 && (
                <div className="mt-4 flex items-center justify-end space-x-2">
                  <button
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                    onClick={() => setRewardsPage(p => Math.max(1, p - 1))}
                    disabled={rewardsPage <= 1}
                  >
                    Prev
                  </button>
                  <span className="text-xs text-gray-500">Page {rewardsPage} of {rewardsPages}</span>
                  <button
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                    onClick={() => setRewardsPage(p => Math.min(rewardsPages, p + 1))}
                    disabled={rewardsPage >= rewardsPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Milestones Tab */}
          {activeTab === 'milestones' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-medium text-gray-900">Milestones</h3>
                {milestonesTotal > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                    {milestonesTotal} total
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowCreateMilestone(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Milestone
              </button>
            </div>
            <div className="p-6">
              {milestonesLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <p className="mt-2 text-gray-500">Loading milestones...</p>
                </div>
              ) : milestonesError ? (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">
                  {milestonesError}
                </div>
              ) : milestones.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h4 className="mt-4 text-sm font-medium text-gray-900">No milestones found</h4>
                  <p className="mt-1 text-sm text-gray-500">No milestones are currently linked to this card.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {milestones.map((milestone) => (
                    <div key={milestone._id} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {milestone.milestoneType.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900">{milestone.description}</h4>
                          <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              milestone.milestoneType === 'welcome' ? 'bg-green-100 text-green-800' :
                              milestone.milestoneType === 'monthly' ? 'bg-blue-100 text-blue-800' :
                              milestone.milestoneType === 'quarterly' ? 'bg-purple-100 text-purple-800' :
                              milestone.milestoneType === 'annually' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              {milestone.milestoneType.charAt(0).toUpperCase() + milestone.milestoneType.slice(1).replace('_', ' ')}
                            </span>
                            <span>Spend: ₹{milestone.spendThreshold.toLocaleString()}</span>
                            <span>{milestone.rps.toLocaleString()} RPs</span>
                            <span>{milestone.benefits.length} benefit{milestone.benefits.length !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          milestone.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {milestone.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          className="ml-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
                          onClick={() => navigate(`/milestones/${milestone._id}`)}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Milestones pagination */}
              {milestonesPages > 1 && (
                <div className="mt-4 flex items-center justify-end space-x-2">
                  <button
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                    onClick={() => setMilestonesPage(p => Math.max(1, p - 1))}
                    disabled={milestonesPage <= 1}
                  >
                    Prev
                  </button>
                  <span className="text-xs text-gray-500">Page {milestonesPage} of {milestonesPages}</span>
                  <button
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                    onClick={() => setMilestonesPage(p => Math.min(milestonesPages, p + 1))}
                    disabled={milestonesPage >= milestonesPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Links Tab */}
          {activeTab === 'links' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Links</h3>
                  <p className="mt-1 text-xs text-gray-500">URLs and notes associated with this card</p>
                </div>

                {isAdmin && (
                  <div className="flex items-center gap-2">
                    {!cardLink ? (
                      <button
                        disabled={cardLinkLoading || cardLinkSaving}
                        onClick={async () => {
                          if (!card?._id) return;
                          try {
                            setCardLinkSaving(true);
                            setCardLinkError(null);
                            const res = await CardsLinkService.create({
                              cardId: card._id,
                              bank_name: card.bankName,
                              credit_name: card.name,
                              entries: [],
                            });
                            setCardLink(res.data || null);
                            setCardLinkBankName(res.data?.bank_name || card.bankName || '');
                            setCardLinkCreditName(res.data?.credit_name || card.name || '');
                            setCardLinkEditing(false);
                          } catch (e) {
                            setCardLinkError(e instanceof Error ? e.message : 'Failed to create links');
                          } finally {
                            setCardLinkSaving(false);
                          }
                        }}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        Create Links
                      </button>
                    ) : cardLinkEditing ? (
                      <>
                        <button
                          disabled={cardLinkSaving}
                          onClick={() => {
                            setCardLinkEditing(false);
                            setCardLinkBankName(cardLink.bank_name || card.bankName || '');
                            setCardLinkCreditName(cardLink.credit_name || card.name || '');
                          }}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                        <button
                          disabled={cardLinkSaving}
                          onClick={async () => {
                            try {
                              setCardLinkSaving(true);
                              setCardLinkError(null);
                              const res = await CardsLinkService.update(cardLink._id, {
                                bank_name: cardLinkBankName.trim(),
                                credit_name: cardLinkCreditName.trim(),
                              });
                              setCardLink(res.data || null);
                              setCardLinkEditing(false);
                            } catch (e) {
                              setCardLinkError(e instanceof Error ? e.message : 'Failed to update links');
                            } finally {
                              setCardLinkSaving(false);
                            }
                          }}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          Save
                        </button>
                      </>
                    ) : (
                      <button
                        disabled={cardLinkLoading}
                        onClick={() => setCardLinkEditing(true)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="p-6 space-y-4">
                {cardLinkError && (
                  <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{cardLinkError}</div>
                )}

                {cardLinkLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-500">Loading links...</p>
                  </div>
                ) : !cardLink ? (
                  <div className="text-center py-10">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 015.656 5.656l-3 3a4 4 0 01-5.656 0m-1.172-1.172a4 4 0 01-5.656-5.656l3-3a4 4 0 015.656 0" />
                    </svg>
                    <h4 className="mt-3 text-sm font-medium text-gray-900">No links yet</h4>
                    <p className="mt-1 text-sm text-gray-500">Create a Links document to add URLs and notes.</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                        <input
                          value={cardLinkEditing ? cardLinkBankName : (cardLink.bank_name || '')}
                          onChange={(e) => setCardLinkBankName(e.target.value)}
                          readOnly={!cardLinkEditing}
                          className={`block w-full px-3 py-2 border rounded-md shadow-sm ${
                            cardLinkEditing ? 'border-gray-300 bg-white' : 'border-gray-200 bg-gray-50'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Credit Name</label>
                        <input
                          value={cardLinkEditing ? cardLinkCreditName : (cardLink.credit_name || '')}
                          onChange={(e) => setCardLinkCreditName(e.target.value)}
                          readOnly={!cardLinkEditing}
                          className={`block w-full px-3 py-2 border rounded-md shadow-sm ${
                            cardLinkEditing ? 'border-gray-300 bg-white' : 'border-gray-200 bg-gray-50'
                          }`}
                        />
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-900">Entries</h4>
                      </div>

                      {Array.isArray(cardLink.entries) && cardLink.entries.length > 0 ? (
                        <div className="mt-3 space-y-2">
                          {cardLink.entries.map((entry: CardLinkEntry, idx: number) => {
                            const entryId = String(entry._id || entry.id || idx);
                            const isEditingEntry = editingEntryId === entryId;
                            const type = String(entry.type || '').toLowerCase();
                            const display = type === 'url' ? entry.url : entry.content;

                            return (
                              <div key={entryId} className="border border-gray-200 rounded-md p-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                        {type || 'unknown'}
                                      </span>
                                      <span className="text-xs text-gray-400 break-all">{entry._id || entry.id || ''}</span>
                                    </div>

                                    {!isEditingEntry ? (
                                      <div className="mt-2 text-sm text-gray-800 break-words">
                                        {type === 'url' && entry.url ? (
                                          <a
                                            href={entry.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-600 hover:underline break-all"
                                          >
                                            {entry.url}
                                          </a>
                                        ) : (
                                          <div className="mt-1 whitespace-pre-wrap break-words rounded-md bg-gray-50 border border-gray-200 p-3 max-h-96 overflow-auto">
                                            {display || '—'}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="mt-3 space-y-2">
                                        <div className="flex items-center gap-2">
                                          <label className="text-xs text-gray-600">Type</label>
                                          <select
                                            value={editingEntryType}
                                            onChange={(e) => setEditingEntryType(e.target.value)}
                                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                                          >
                                            <option value="url">url</option>
                                            <option value="content">content</option>
                                          </select>
                                        </div>
                                        {String(editingEntryType).toLowerCase() === 'url' ? (
                                          <input
                                            value={editingEntryValue}
                                            onChange={(e) => setEditingEntryValue(e.target.value)}
                                            placeholder="https://..."
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                          />
                                        ) : (
                                          <textarea
                                            value={editingEntryValue}
                                            onChange={(e) => setEditingEntryValue(e.target.value)}
                                            rows={4}
                                            placeholder="Enter content..."
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                          />
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {isAdmin && (
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {isEditingEntry ? (
                                        <>
                                          <button
                                            disabled={entrySaving}
                                            onClick={() => {
                                              setEditingEntryId(null);
                                              setEditingEntryType('url');
                                              setEditingEntryValue('');
                                            }}
                                            className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-60"
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            disabled={entrySaving}
                                            onClick={async () => {
                                              const realEntryId = String(entry._id || entry.id || '');
                                              if (!realEntryId) return;
                                              try {
                                                setEntrySaving(true);
                                                setCardLinkError(null);
                                                const payload: any = { type: editingEntryType };
                                                if (String(editingEntryType).toLowerCase() === 'url') payload.url = editingEntryValue.trim();
                                                else payload.content = editingEntryValue;
                                                const res = await CardsLinkService.updateEntry(cardLink._id, realEntryId, payload);
                                                setCardLink(res.data || null);
                                                setEditingEntryId(null);
                                              } catch (e) {
                                                setCardLinkError(e instanceof Error ? e.message : 'Failed to update entry');
                                              } finally {
                                                setEntrySaving(false);
                                              }
                                            }}
                                            className="px-3 py-1 text-sm border border-transparent rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                          >
                                            Save
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            onClick={() => {
                                              const realEntryId = String(entry._id || entry.id || '');
                                              if (!realEntryId) return;
                                              setEditingEntryId(entryId);
                                              setEditingEntryType((entry.type as any) || 'url');
                                              setEditingEntryValue(String(type === 'url' ? entry.url || '' : entry.content || ''));
                                            }}
                                            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                                          >
                                            Edit
                                          </button>
                                          <button
                                            disabled={entrySaving}
                                            onClick={async () => {
                                              const realEntryId = String(entry._id || entry.id || '');
                                              if (!realEntryId) return;
                                              if (!window.confirm('Delete this entry?')) return;
                                              try {
                                                setEntrySaving(true);
                                                setCardLinkError(null);
                                                const res = await CardsLinkService.deleteEntry(cardLink._id, realEntryId);
                                                setCardLink(res.data || null);
                                              } catch (e) {
                                                setCardLinkError(e instanceof Error ? e.message : 'Failed to delete entry');
                                              } finally {
                                                setEntrySaving(false);
                                              }
                                            }}
                                            className="px-3 py-1 text-sm border border-red-200 rounded text-red-700 hover:bg-red-50 disabled:opacity-60"
                                          >
                                            Delete
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="mt-3 text-sm text-gray-500">No entries yet.</div>
                      )}

                      {isAdmin && (
                        <div className="mt-5 border-t border-gray-200 pt-4">
                          <h5 className="text-sm font-semibold text-gray-900">Add entry</h5>
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-4 gap-3 items-start">
                            <div>
                              <label className="block text-xs font-medium text-gray-700">Type</label>
                              <select
                                value={newEntryType}
                                onChange={(e) => setNewEntryType(e.target.value)}
                                className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                              >
                                <option value="url">url</option>
                                <option value="content">content</option>
                              </select>
                            </div>
                            <div className="sm:col-span-3">
                              <label className="block text-xs font-medium text-gray-700">Value</label>
                              {String(newEntryType).toLowerCase() === 'url' ? (
                                <input
                                  value={newEntryValue}
                                  onChange={(e) => setNewEntryValue(e.target.value)}
                                  placeholder="https://..."
                                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                />
                              ) : (
                                <textarea
                                  value={newEntryValue}
                                  onChange={(e) => setNewEntryValue(e.target.value)}
                                  rows={3}
                                  placeholder="Enter content..."
                                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                />
                              )}
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-end">
                            <button
                              disabled={entrySaving || cardLinkSaving}
                              onClick={async () => {
                                const v = newEntryValue.trim();
                                if (!v) return;
                                try {
                                  setEntrySaving(true);
                                  setCardLinkError(null);
                                  const payload: any = { type: newEntryType };
                                  if (String(newEntryType).toLowerCase() === 'url') payload.url = v;
                                  else payload.content = newEntryValue;
                                  const res = await CardsLinkService.addEntry(cardLink._id, payload);
                                  setCardLink(res.data || null);
                                  setNewEntryValue('');
                                } catch (e) {
                                  setCardLinkError(e instanceof Error ? e.message : 'Failed to add entry');
                                } finally {
                                  setEntrySaving(false);
                                }
                              }}
                              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right column - Reference and actions */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Quick Stats</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-center">
                <div className="w-16 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <svg className="w-8 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z"/>
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-gray-900">{card.name}</h4>
                <p className="text-sm text-gray-500">{card.bankName}</p>
              </div>
              
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Status</span>
                  {isEditing ? (
                    <div className="flex items-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.active !== undefined ? formData.active : card.active}
                          onChange={(e) => handleInputChange('active', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">Active</span>
                      </label>
                    </div>
                  ) : (
                    <span className={`text-sm font-medium ${card.active ? 'text-green-600' : 'text-red-600'}`}>
                      {card.active ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Verified</span>
                  <span className={`text-sm font-medium ${card.isVerify === 'true' ? 'text-blue-600' : 'text-yellow-600'}`}>
                    {card.isVerify === 'true' ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Categories</span>
                  <span className="text-sm font-medium text-gray-900">{card.categories.length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Reference Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Reference</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <input
                  type="text"
                  value={card.reference?.source || 'Not specified'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-sm"
                  readOnly
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fetched At</label>
                <input
                  type="text"
                  value={card.reference?.fetchedAt ? new Date(card.reference.fetchedAt).toLocaleString() : 'Not specified'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-sm"
                  readOnly
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Disclaimer</label>
                <textarea
                  value={card.reference?.disclaimer || 'No disclaimer provided'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-sm"
                  rows={3}
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Actions</h3>
            </div>
            <div className="p-6 space-y-3">
              {!isEditing && (
                <>
                  <button className="w-full bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
                    Mark as Verified
                  </button>
                  
                </>
              )}
              {isEditing && (
                <div className="text-center text-sm text-gray-500 py-4">
                  <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Currently editing card details.
                  <br />
                  Save or cancel to access other actions.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reward Detail Modal */}
      {selectedReward && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-30" onClick={closeRewardDetail}></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-xl mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">{selectedReward.name}</h3>
              <button onClick={closeRewardDetail} className="text-gray-400 hover:text-gray-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                  {selectedReward.logo?.url ? (
                    <img src={selectedReward.logo.url} alt={selectedReward.logo.alt || selectedReward.name} className="h-12 w-12 object-cover" />
                  ) : (
                    <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v16H4z" />
                    </svg>
                  )}
                </div>
                <div>
                  <div className="text-sm text-gray-500">{selectedReward.categoryPrimary} {selectedReward.subcategory ? `• ${selectedReward.subcategory}` : ''}</div>
                  <div className="text-sm text-gray-900 font-medium">{selectedReward.rewards?.description}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500">Type</div>
                  <div className="text-sm text-gray-900">{selectedReward.rewards?.type || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Value</div>
                  <div className="text-sm text-gray-900">{selectedReward.rewards?.value} {selectedReward.rewards?.unit}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Min Spend</div>
                  <div className="text-sm text-gray-900">{selectedReward.rewards?.minSpend ? `₹${selectedReward.rewards.minSpend}` : '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Max Spend</div>
                  <div className="text-sm text-gray-900">{selectedReward.rewards?.maxSpend ? `₹${selectedReward.rewards.maxSpend}` : '—'}</div>
                </div>
              </div>

              {selectedReward.tags && selectedReward.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedReward.tags.map((t: string, i: number) => (
                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button onClick={closeRewardDetail} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Reward Modal */}
      {showCreateReward && card && (
        <CreateRewardForm
          cardId={card._id}
          cardName={card.name}
          bankName={card.bankName}
          bankSlug={card.bankSlug}
          cardSlug={card.slug}
          onClose={() => setShowCreateReward(false)}
          onSuccess={handleCreateRewardSuccess}
        />
      )}

      {/* Create Milestone Modal */}
      {showCreateMilestone && card && (
        <CreateMilestoneForm
          cardId={card._id}
          cardName={card.name}
          bankName={card.bankName}
          bankSlug={card.bankSlug}
          cardSlug={card.slug}
          onClose={() => setShowCreateMilestone(false)}
          onSuccess={handleCreateMilestoneSuccess}
        />
      )}
    </div>
  );
};

export default CardDetail;
